// SidebarSwitcher.jsx

import React, { useEffect, useState } from 'react';

// Context
import { useDatabase } from 'context/DatabaseContext';
import { useNavigation } from 'context/NavigationContext';
import { useUser } from 'context/UserContext';
import { useWorkspace } from 'context/WorkspaceContext';

// Firebase
import { signOut } from 'firebase/auth';
import { auth } from 'firebaseConfig';

// Routing
import { useLocation, useNavigate } from 'react-router-dom';

// Utilities
import { SectionType } from 'utilities/Enums';
import { getSectionPath } from 'utilities/Routing';

// Services
import { createSetting, updateSetting } from 'services/settings/SettingWriteService';
import { subscribeToWorkspaces } from 'services/workspaces/WorkspaceReadService';

// CSS
import './SidebarSwitcher.css';

// Components
import SwitcherToggle from 'main/sidebar/switcher/toggle/SwitcherToggle';
import TemplatePublishModal from 'templates/publish/TemplatePublishModal';
import WorkspaceAddModal from 'workspaces/add/WorkspaceAddModal';

/**
 * SidebarSwitcher
 * 
 * Allows the user to switch between workspaces in the desktop sidebar.
 *  
 * @returns {ReactNode} - SidebarSwitcher.
 */
const SidebarSwitcher = React.memo(() => {

    /**
     * getWorkspaceKey
     * 
     * Utility function to extract a consistent workspace key from a workspace object.
     * Prioritizes the 'key' field, but falls back to 'id' if 'key' is not present.
     * Returns null if neither is available.
     * 
     * @param {object} workspace - The workspace object to extract the key from.
     * 
     * @returns {string|null} - The extracted workspace key or null if not found.
     */
    const getWorkspaceKey = (workspace) => {
        const key = String(workspace?.key || '').trim();
        if (key) return key;
        return String(workspace?.id || '').trim();
    };

    // ----- CONTEXT ----- //

    // Global Context
    const { setCurrentSection } = useNavigation();

    // Database Context
    const { setActiveDatabase } = useDatabase();

    // User Context
    const {
        user,
        userSettings,
        setUser,
        setUserSettings,
    } = useUser();

    // Workspace Context
    const { activeWorkspace, setActiveWorkspace } = useWorkspace();

    // ----- ROUTING ----- //

    // Navigate Router Hook
    const navigate = useNavigate();
    const location = useLocation();

    // ----- STATE ----- //

    // Add Space Modal Open State
    const [addOpen, setAddOpen] = useState(false);

    // Publish Template Modal Open State
    const [publishOpen, setPublishOpen] = useState(false);

    // Workspaces State
    const [workspaces, setWorkspaces] = useState([]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!user?.key) {
            setWorkspaces([]);
            return;
        }

        const unsubscribe = subscribeToWorkspaces(user.key, (rows) => {
            setWorkspaces(Array.isArray(rows) ? rows : []);
        });

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [user?.key]);

    // ----- HANDLERS ----- //

    /**
     * Handles toggle click in the switcher trigger.
     */
    const handleToggleClick = () => {};

    /**
     * Handles selecting a workspace and applying it to user settings.
     *
     * @param {object} workspace - The selected workspace object.
     */
    const handleWorkspaceSelect = async (workspace) => {
        if (!user?.key) return;

        const workspaceKey = getWorkspaceKey(workspace);
        if (!workspaceKey) return;

        const normalizedWorkspace = {
            ...(workspace || {}),
            key: workspaceKey,
        };

        const homePath = getSectionPath(SectionType.HOME);
        const currentWorkspaceKey = activeWorkspace?.key || userSettings?.ACTIVE_WORKSPACE_KEY || null;
        const isSwitchingWorkspace = Boolean(workspaceKey && workspaceKey !== currentWorkspaceKey);

        setUserSettings && setUserSettings({
            ...(userSettings || {}),
            ACTIVE_WORKSPACE_KEY: workspaceKey,
            userKey: user.key,
        });
        setActiveWorkspace && setActiveWorkspace(normalizedWorkspace);

        if (isSwitchingWorkspace) {
            setActiveDatabase && setActiveDatabase(null);
        }

        setCurrentSection && setCurrentSection(SectionType.HOME);

        try {
            try {
                if (userSettings?.id || userSettings?.userKey) {
                    await updateSetting(user.key, { ACTIVE_WORKSPACE_KEY: workspaceKey });
                } else {
                    await createSetting(user.key, { ACTIVE_WORKSPACE_KEY: workspaceKey });
                }
            } catch (error) {
                if (error?.code === 'not-found') {
                    await createSetting(user.key, { ACTIVE_WORKSPACE_KEY: workspaceKey });
                } else {
                    throw error;
                }
            }
        } catch (error) {
            console.error('[SidebarSwitcher] update ACTIVE_WORKSPACE_KEY error:', error);
        }

        if (location.pathname !== homePath) {
            navigate(homePath, { replace: true });

            // Fallback for route-lock edge cases where SPA navigation can stick on deep pages.
            window.setTimeout(() => {
                if (window.location.pathname !== homePath) {
                    window.location.assign(homePath);
                }
            }, 75);

            return;
        }

        navigate(homePath);
    };

    /**
     * Opens the add workspace modal.
     */
    const handleWorkspaceAdd = () => {
        setAddOpen(true);
    };

    /**
     * Opens the publish template modal for the active workspace.
     */
    const handlePublishTemplate = async () => {
        if (!activeWorkspace?.key) return;
        setPublishOpen(true);
    };

    /**
     * Signs the user out and routes to the login screen.
     */
    const handleLogout = async () => {

        // Log out
        await signOut(auth);

        setUser(null);

        navigate("/");
    };

    // ----- RETURN EARLY ----- //

    if (!user) return null;

    // ----- RENDER ----- //

    return (
        <>

            {/* SWITCHER CONTAINER */}
            <div className="sidebar-switcher-container">

                {/* TOGGLE */}
                <SwitcherToggle
                    onClick={handleToggleClick}
                    onWorkspaceSelect={handleWorkspaceSelect}
                    onWorkspaceAdd={handleWorkspaceAdd}
                    onPublishTemplate={handlePublishTemplate}
                    onLogout={handleLogout}
                    workspaces={workspaces}
                    activeWorkspaceKey={userSettings?.ACTIVE_WORKSPACE_KEY || null}
                />

                {/* ADD SPACE MODAL */}
                <WorkspaceAddModal
                    isOpen={addOpen}
                    setOpen={setAddOpen}
                    onSave={(workspace) => {
                        if (workspace?.key) {
                            handleWorkspaceSelect(workspace);
                        }
                    }}
                />

                {/* TEMPLATE PUBLISH MODAL */}
                <TemplatePublishModal
                    isOpen={publishOpen}
                    setOpen={setPublishOpen}
                    workspace={activeWorkspace}
                />

            </div>
        </>
    );

});

// Dev Tools
SidebarSwitcher.displayName = 'SidebarSwitcher';

export default SidebarSwitcher;



